/**
 * af-main-collection.js — And Friends collection experience (R03)
 *
 * Enhances the Liquid-rendered section:
 * - PRODUCT/MODEL view toggle, remembered on-device
 * - Filter/sort/reset updates in place via the Section Rendering API
 * - Load-more appends the next page into the grid
 * - Inline mobile filter panel (sort/filter 2.0), open-group preservation
 *   across updates
 * - Live cart count + device-local wishlist count
 *
 * Everything falls back to normal navigation. No dependencies.
 *
 * v1.15 (R56) — THIS PAGE DOES NOT SCROLL ON THE WINDOW.
 * Horizon sets 'overflow: hidden' on both <html> and <body> and does the
 * scrolling inside 'div.page-wrapper' (overflow-y: auto). So every
 * window.scrollY / scrollTo / scrollBy written in R54 and R55 read 0 and
 * did nothing: the scroll was never restored on a refresh, and the R55
 * prepend never held its anchor either. Measured live 4 Sep 2026 —
 * window.scrollY 0 and document.body.scrollHeight 902 against a real
 * .page-wrapper scrollHeight of 22688.
 *
 * The scroll container is now RESOLVED rather than assumed: walk up from
 * the section to the nearest scrollable ancestor, and fall back to the
 * window when there isn't one (so this still works in a theme that scrolls
 * normally, and in the editor). Nothing else reads window.scrollY.
 *
 * Horizon also sets history.scrollRestoration = 'manual', so the browser
 * restores nothing by itself on a reload — our restore is the ONLY one
 * running, and it re-applies across frames until the position sticks,
 * because lazy product images grow the container after the cards land and
 * a single early set just clamps short.
 *
 * v1.14 (R55) — a bare '?page=N' link is restored too.
 * R54 taught the section to rebuild itself from 'af_page=N', which is what
 * Load More now writes. But '?page=N' URLs still exist and still landed on
 * page N alone: links shared before R54, anything already in Google's
 * index, a bookmark, a customer who edits the URL, and any theme or app
 * link that points straight at a page. Those got exactly the dead end R54
 * was meant to remove.
 *
 * They are handled from the other end. The server has already rendered
 * page N, so rather than re-fetch it, pages 1..N-1 are fetched and
 * PREPENDED above it — and the scroll is compensated by the exact height
 * that inserts, measured off a real card, so the visitor keeps looking at
 * the product they were already looking at instead of being thrown to the
 * top of ninety newly-arrived ones. The URL is then rewritten to
 * 'af_page=N', so from that moment the page is on the R54 path and the
 * next refresh costs nothing extra.
 *
 * v1.13 (R54) — LOAD MORE SURVIVES A REFRESH, on collection AND search.
 * Until now loadMore() did history.replaceState(..., paginate.next.url), so
 * after one click the address bar read '?page=2' while the grid held pages
 * 1+2. Refresh, share that link, or come back to it and Shopify rendered
 * page 2 ALONE — the first 45 products simply gone, with no control to get
 * back to them. The page number was recording "the last page I fetched"
 * into a slot that means "the only page to render".
 *
 * The fix separates those two ideas. The address bar now carries
 * 'af_page=N' — "N pages have been loaded" — and never 'page'. A reload
 * therefore renders page 1 (complete, correct, and what a no-JS visitor or
 * a crawler gets), and this file appends pages 2..N on boot, then puts the
 * visitor back at the scroll offset they left from. 'page' is still what
 * gets FETCHED; it just no longer decides what the server renders on the
 * way back in.
 *
 * Also fixed here: navigate() now strips af_page alongside page (a filter
 * change must not inherit the old result set's page count), and its
 * pushState moved AHEAD of the DOM swap, because the incoming instance
 * boots on the URL that is current at swap time — it was reading the old
 * one.
 *
 * v1.12.1 — the floating bar stands DOWN while a mobile nav menu is open
 * (af-nav:menu events): no stacking-context fight can put the filter
 * over the burger menu.
 * v1.12 — synchronised reveal: when a sticky AF nav is on the page, the
 * mobile floating filter bar mirrors the nav's 'af-nav:dock' events and
 * the two appear/hide in the same frame. Without an AF nav the old
 * self-detection still runs.
 */

const VIEW_KEY = 'af:view';
const COLS_KEY = 'af:cols';
/* R54: how many pages are on screen, kept in the URL under our OWN key so
   Shopify never mistakes it for "render this page and nothing else". */
const PAGES_PARAM = 'af_page';
const SCROLL_KEY = 'af:mc:scroll:';
/* A hand-typed af_page=999 must not turn one page view into 999 fetches. */
const MAX_BACKFILL = 12;
/* R33: the wishlist count was dead four ways over.
   1. It read localStorage['af:wishlist']; the engine writes 'af-wishlist:v1'.
   2. It listened for 'af:wishlist:updated'; the engine fires 'af:wishlist'.
   3. In FRCP mode — the live mode, with the Froonze embed on — the saved list
      is not in localStorage at all. It is in window.frcp.wishlist.data.
   4. The engine only ships with the heart (af-wishlist-button) and the
      wishlist section, neither of which renders on a collection or search
      page. So on exactly the pages that show this badge, window.AFWishlist
      never exists and no af:wishlist event is ever fired.
   Hence readCount(): ask the engine if it happens to be there, else read
   Froonze directly the same way the engine does, else fall back to the
   (correct) localStorage key. Froonze loads async, so the boot poll below
   re-reads until its data lands. */
const WISHLIST_KEY = 'af-wishlist:v1';

class AFMainCollection extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.ajaxEnabled = this.dataset.ajax === 'true';
    this.mpanel = this.querySelector('[data-af-mpanel]');

    this.initView();
    this.initCols();
    this.initControlsReveal();
    this.initRailAlign();
    this.updateWishlistCount();

    /* Panel toggle + view toggle + counters work regardless of AJAX setting */
    this.addEventListener('click', (e) => {
      if (e.target.closest('[data-af-mpanel-toggle]')) this.toggleMpanel();
      const viewBtn = e.target.closest('[data-af-view]');
      if (viewBtn) this.setView(viewBtn.dataset.afView);
      const colBtn = e.target.closest('[data-af-cols]');
      if (colBtn) this.setCols(colBtn.dataset.afCols);
    });

    /* Focus the search input when its disclosure opens */
    this.querySelectorAll('[data-af-search]').forEach((d) => {
      d.addEventListener('toggle', () => {
        if (d.open) d.querySelector('input')?.focus();
      });
    });

    /* Cart / wishlist live updates */
    if (!AFMainCollection.globalBound) {
      AFMainCollection.globalBound = true;
      ['cart:update', 'cart:updated', 'cart:change'].forEach((name) =>
        document.addEventListener(name, () => AFMainCollection.refreshCartCounts())
      );
      /* both names: 'af:wishlist' is what the engine actually fires, the
         other is kept so nothing that already dispatches it breaks. */
      ['af:wishlist', 'af:wishlist:updated'].forEach((name) =>
        document.addEventListener(name, () => AFMainCollection.refreshWishlistCounts())
      );
      AFMainCollection.bootWishlistCount();
      window.addEventListener('pageshow', (e) => {
        if (e.persisted) AFMainCollection.refreshCartCounts();
      });
      window.addEventListener('popstate', (e) => {
        if (e.state && e.state.afMain) {
          document.querySelector('af-main-collection')?.navigate(window.location.href, { push: false });
        }
      });
    }

    if (!this.ajaxEnabled) return;

    /* Filter links (values, reset, empty-state reset) */
    this.addEventListener('click', (e) => {
      const link = e.target.closest('a[data-af-filter-url]');
      if (link && link.getAttribute('aria-disabled') !== 'true') {
        e.preventDefault();
        this.navigate(link.href);
        return;
      }
      const more = e.target.closest('a[data-af-loadmore]');
      if (more) {
        e.preventDefault();
        this.loadMore(more);
      }
    });

    /* Sort forms (buttons carry the value) + price forms */
    this.querySelectorAll('[data-af-sort-form]').forEach((form) => {
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.navigate(this.urlFromForm(form, e.submitter));
      });
    });
    this.querySelectorAll('[data-af-price-form]').forEach((form) => {
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.navigate(this.urlFromForm(form));
      });
    });

    this.initRestore();
  }

  /* ---------- View toggle ---------- */

  initView() {
    // In the theme editor the "Default view" setting always applies; a
    // visitor's remembered toggle choice only outranks it on the live
    // storefront (that's the remember-on-device feature working).
    let view = null;
    if (!window.Shopify?.designMode) {
      try {
        view = localStorage.getItem(VIEW_KEY);
      } catch {}
    }
    this.setView(view || this.dataset.defaultView || 'product', { store: false });
  }

  setView(view, { store = true } = {}) {
    if (view !== 'model' && view !== 'product') return;
    this.dataset.view = view;
    this.querySelectorAll('[data-af-view]').forEach((btn) => {
      btn.setAttribute('aria-pressed', btn.dataset.afView === view ? 'true' : 'false');
    });
    if (store) {
      try {
        localStorage.setItem(VIEW_KEY, view);
      } catch {}
    }
  }

  /* ---------- Mobile column switcher ---------- */

  initCols() {
    // Same model as the view toggle: the editor follows the section setting,
    // the live storefront remembers the customer's own choice on-device.
    let cols = null;
    if (!window.Shopify?.designMode) {
      try {
        cols = localStorage.getItem(COLS_KEY);
      } catch {}
    }
    this.setCols(cols || this.dataset.colsDefault || '2', { store: false });
  }

  setCols(cols, { store = true } = {}) {
    if (cols !== '1' && cols !== '2') return;
    this.dataset.cols = cols;
    this.querySelectorAll('[data-af-cols]').forEach((btn) => {
      btn.setAttribute('aria-pressed', btn.dataset.afCols === cols ? 'true' : 'false');
    });
    if (store) {
      try {
        localStorage.setItem(COLS_KEY, cols);
      } catch {}
    }
  }

  /* ---------- Mobile scroll-up controls reveal ---------- */

  /* ---------- Desktop rail alignment (v1.11) ----------
     "First row of products": nudge the rail down so its top sits level
     with the grid's first images. Measured live (loop-safe: the current
     offset is subtracted back out), re-run on resize. */

  initRailAlign() {
    if (this.dataset.railAlign !== 'grid') return;
    this._railOffset = 0;
    this._alignRail = () => {
      const rail = this.querySelector('.af-mc__rail');
      const grid = this.querySelector('[data-af-grid]');
      if (!rail || !grid) return;
      if (window.innerWidth < 750) {
        this.style.removeProperty('--af-rail-offset');
        this._railOffset = 0;
        return;
      }
      const railNaturalTop = rail.getBoundingClientRect().top - this._railOffset;
      const offset = Math.max(0, Math.round(grid.getBoundingClientRect().top - railNaturalTop));
      this._railOffset = offset;
      this.style.setProperty('--af-rail-offset', `${offset}px`);
    };
    requestAnimationFrame(this._alignRail);
    window.addEventListener('resize', this._alignRail);
    window.addEventListener('load', this._alignRail);
  }

  initControlsReveal() {
    this.controlsEl = this.querySelector('[data-af-controls]');
    if (!this.controlsEl || this.dataset.controlsReveal !== 'true') return;

    /* v1.12: when a sticky AF nav is on the page, IT is the single source
       of truth — the filter bar mirrors the nav's dock events and both
       appear/disappear in the same frame. Two independent scroll
       detectors could never agree (the filter led on fast up-scrolls and
       never fired on slow ones — its 3px per-tick filter ate them). */
    if (document.querySelector('af-nav[data-sticky]')) {
      this._navMenuOpen = false;
      this._onNavMenu = (event) => {
        this._navMenuOpen = !!event.detail?.open;
        if (this._navMenuOpen) this.unfloatControls();
      };
      document.addEventListener('af-nav:menu', this._onNavMenu);
      this._onNavDock = (event) => {
        const el = this.controlsEl;
        if (!el) return;
        if (this._navMenuOpen) return;
        if (window.Shopify?.designMode) {
          this.unfloatControls();
          return;
        }
        if (this.hasAttribute('data-mpanel-open')) return;
        if (window.innerWidth >= 750) {
          this.unfloatControls();
          return;
        }
        if (event.detail?.shown) {
          // Only float once the natural controls have scrolled off the top.
          const probe = this._controlsSpacer || el;
          if (probe.getBoundingClientRect().bottom < 0) this.floatControls();
        } else {
          this.unfloatControls();
        }
      };
      document.addEventListener('af-nav:dock', this._onNavDock);
      return;
    }

    /* R56: same lesson as the restore — listen on whatever actually
       scrolls. On Horizon that is .page-wrapper, and a window 'scroll'
       listener here never fired at all. */
    this._controlsScrollTarget = this.scroller() || window;
    this._controlsLastY = this.scrollPos();
    this._onControlsScroll = () => {
      if (this._controlsRaf) return;
      this._controlsRaf = requestAnimationFrame(() => {
        this._controlsRaf = null;
        this.updateControlsFloat();
      });
    };
    this._controlsScrollTarget.addEventListener('scroll', this._onControlsScroll, { passive: true });
  }

  updateControlsFloat() {
    const el = this.controlsEl;
    if (!el) return;
    /* Editor scroll jumps strand the floating bar mid-preview (same lesson
       as the nav) — preview shows the in-flow controls only (v1.10). */
    if (window.Shopify?.designMode) {
      this.unfloatControls();
      return;
    }
    /* The open filter panel lives in the page flow — never float over it */
    if (this.hasAttribute('data-mpanel-open')) return;
    if (window.innerWidth >= 750) {
      this.unfloatControls();
      return;
    }
    const y = this.scrollPos();
    const dy = y - this._controlsLastY;
    this._controlsLastY = y;
    if (Math.abs(dy) < 3) return;

    const floating = el.classList.contains('is-floating');
    if (!floating && dy < 0) {
      // Only float once the natural controls have scrolled off the top.
      if (el.getBoundingClientRect().bottom < 0) this.floatControls();
    } else if (floating) {
      if (dy > 0) {
        this.unfloatControls();
      } else if (this._controlsSpacer) {
        // Back at the natural position: hand back to the in-flow controls.
        const barOffset = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-group-height')) || 0;
        if (this._controlsSpacer.getBoundingClientRect().top >= barOffset) this.unfloatControls();
      }
    }
  }

  floatControls() {
    const el = this.controlsEl;
    if (!el || el.classList.contains('is-floating')) return;
    this._controlsSpacer = document.createElement('div');
    this._controlsSpacer.style.height = `${el.offsetHeight}px`;
    this._controlsSpacer.setAttribute('aria-hidden', 'true');
    el.parentNode?.insertBefore(this._controlsSpacer, el);
    el.classList.add('is-floating');
  }

  unfloatControls() {
    this.controlsEl?.classList.remove('is-floating');
    this._controlsSpacer?.remove();
    this._controlsSpacer = null;
  }

  disconnectedCallback() {
    if (this._alignRail) {
      window.removeEventListener('resize', this._alignRail);
      window.removeEventListener('load', this._alignRail);
    }
    if (this._onControlsScroll) {
      (this._controlsScrollTarget || window).removeEventListener('scroll', this._onControlsScroll);
    }
    if (this._onNavDock) document.removeEventListener('af-nav:dock', this._onNavDock);
    if (this._onNavMenu) document.removeEventListener('af-nav:menu', this._onNavMenu);
    if (this._saveScroll) window.removeEventListener('pagehide', this._saveScroll);
    this.unfloatControls();
  }

  /* ---------- Mobile filter panel (2.0) ---------- */

  toggleMpanel(force) {
    if (!this.mpanel) return;
    const open = typeof force === 'boolean' ? force : this.mpanel.hidden;
    if (open) {
      /* If the floating bar triggered this, hand back to the in-flow
         controls first so the panel opens beneath the toggle, not off-screen */
      if (this.controlsEl?.classList.contains('is-floating')) {
        this.unfloatControls();
        const barOffset = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-group-height')) || 0;
        const top = this.scrollPos() + this.controlsEl.getBoundingClientRect().top - barOffset;
        this.scrollToPos(Math.max(0, top));
      }
      this.mpanel.hidden = false;
      this.setAttribute('data-mpanel-open', '');
    } else {
      this.mpanel.hidden = true;
      this.removeAttribute('data-mpanel-open');
    }
    this.querySelectorAll('[data-af-mpanel-toggle]').forEach((b) =>
      b.setAttribute('aria-expanded', open ? 'true' : 'false')
    );
  }

  /* ---------- Counters ---------- */

  static refreshCartCounts() {
    const el = document.querySelector('af-main-collection');
    const cartUrl = el?.dataset.cartUrl || '/cart';
    fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .then((cart) => {
        if (!cart) return;
        document.querySelectorAll('[data-af-cart-count]').forEach((s) => (s.textContent = cart.item_count));
      })
      .catch(() => {});
  }

  /* How many products are saved, whichever store is actually holding them. */
  static readWishlistCount() {
    try {
      const engine = window.AFWishlist;
      if (engine && typeof engine.list === 'function') return engine.list().length;
    } catch {}
    try {
      const d = window.frcp && window.frcp.wishlist && window.frcp.wishlist.data;
      if (d && d.lists) return Object.keys(d.lists[d.activeList || 'default'] || {}).length;
    } catch {}
    try {
      const list = JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]');
      if (Array.isArray(list)) return list.length;
    } catch {}
    return 0;
  }

  /* document-scoped, not this-scoped: af-search renders the same badge and is
     NOT inside an <af-main-collection>, so a this.querySelectorAll never
     reached it — the search bar's count could not update even in principle. */
  static refreshWishlistCounts() {
    const count = AFMainCollection.readWishlistCount();
    document.querySelectorAll('[data-af-wishlist-count]').forEach((s) => {
      s.textContent = count > 0 ? ` (${count})` : '';
    });
  }

  /* Froonze loads async and there is no engine on these pages to tell us when
     it lands, so re-read on a short decaying poll, and stop early once a count
     appears. Same 12s ceiling the engine uses. */
  static bootWishlistCount(t0 = Date.now()) {
    AFMainCollection.refreshWishlistCounts();
    const settled = AFMainCollection.readWishlistCount() > 0;
    if (!settled && Date.now() - t0 < 12000) {
      setTimeout(() => AFMainCollection.bootWishlistCount(t0), 400);
    }
  }

  updateWishlistCount() {
    AFMainCollection.refreshWishlistCounts();
  }

  /* ---------- Navigation (filters/sort/reset) ---------- */

  urlFromForm(form, submitter) {
    const data = new FormData(form);
    if (submitter && submitter.name) data.set(submitter.name, submitter.value);
    const params = new URLSearchParams(data);
    [...params.entries()].forEach(([k, v]) => {
      if (v === '') params.delete(k);
    });
    const action = form.getAttribute('action') || window.location.pathname;
    return `${action}?${params.toString()}`;
  }

  captureState() {
    return {
      openKeys: [...this.querySelectorAll('details[data-af-group][open]')].map((d) => d.dataset.afGroupKey),
      mpanelOpen: this.hasAttribute('data-mpanel-open'),
    };
  }

  static restoreState(state) {
    const next = document.querySelector('af-main-collection');
    if (!next) return;
    if (state.mpanelOpen) next.toggleMpanel(true);
    state.openKeys.forEach((key) => {
      next.querySelectorAll(`details[data-af-group-key="${CSS.escape(key)}"]`).forEach((d) => (d.open = true));
    });
  }

  async navigate(rawUrl, { push = true } = {}) {
    const url = new URL(rawUrl, window.location.origin);
    url.searchParams.delete('page');
    /* A new filter/sort/reset is a NEW result set — it cannot inherit how
       far the previous one had been paged through. */
    url.searchParams.delete(PAGES_PARAM);

    this.abortController?.abort();
    this.abortController = new AbortController();

    const fetchUrl = new URL(url);
    fetchUrl.searchParams.set('section_id', this.sectionId);

    this.setAttribute('aria-busy', 'true');

    try {
      const res = await fetch(fetchUrl, { signal: this.abortController.signal });
      if (!res.ok) throw new Error(`Section render failed: ${res.status}`);
      const html = await res.text();
      if (!html || !html.includes('af-main-collection')) throw new Error('Unexpected section response');

      const state = this.captureState();
      const wrapper = document.getElementById(`shopify-section-${this.sectionId}`);
      if (!wrapper) throw new Error('Section wrapper not found');

      /* The URL is written BEFORE the swap: the incoming instance's
         connectedCallback reads window.location to decide whether it owes
         the grid a backfill, and at swap time that must already be the new
         URL, not the one being navigated away from. */
      if (push) history.pushState({ afMain: true }, '', url);

      wrapper.outerHTML = html; // new instance boots itself and re-applies the stored view

      AFMainCollection.restoreState(state);

      document.dispatchEvent(new CustomEvent('af:collection:updated', { detail: { url: url.toString() } }));
    } catch (err) {
      if (err.name === 'AbortError') return;
      window.location.assign(url);
    }
  }

  /* ---------- Load more ---------- */

  async loadMore(link) {
    const url = new URL(link.href, window.location.origin);
    const fetchUrl = new URL(url);
    fetchUrl.searchParams.set('section_id', this.sectionId);

    link.setAttribute('aria-busy', 'true');

    try {
      const res = await fetch(fetchUrl);
      if (!res.ok) throw new Error(`Load more failed: ${res.status}`);
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      const newCells = doc.querySelectorAll('[data-af-cell]');
      const grid = this.querySelector('[data-af-grid]');
      if (!grid || newCells.length === 0) throw new Error('No products in response');

      const firstNew = newCells[0];
      newCells.forEach((cell) => grid.appendChild(cell));

      const nextWrap = doc.querySelector('[data-af-loadmore-wrap]');
      const currentWrap = this.querySelector('[data-af-loadmore-wrap]');
      if (currentWrap && nextWrap) currentWrap.innerHTML = nextWrap.innerHTML;

      /* Record the page COUNT, not a page number. Writing 'page=2' here is
         what used to strand a refresh on page 2 with page 1 unreachable. */
      this.markPagesLoaded(parseInt(url.searchParams.get('page'), 10) || 1);

      const live = this.querySelector('[data-af-live]');
      if (live) live.textContent = `${newCells.length} more products loaded`;
      grid.querySelector(`[data-af-cell]:nth-last-child(${newCells.length}) a`)?.focus({ preventScroll: true });
      firstNew.scrollIntoView({ block: 'nearest' });
    } catch (err) {
      window.location.assign(url);
    }
  }

  /* ---------- Which element actually scrolls (R56) ----------
     Horizon scrolls .page-wrapper, not the window. Resolve it instead of
     assuming either one. Not cached while the answer is "the window":
     early in boot a short page has nothing scrollable yet, and caching
     that would pin us to the wrong element for the whole session. */

  scroller() {
    if (this._scroller && this._scroller.isConnected) return this._scroller;
    let el = this.parentElement;
    while (el && el !== document.body && el !== document.documentElement) {
      const overflowY = getComputedStyle(el).overflowY;
      if ((overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight + 1) {
        this._scroller = el;
        return el;
      }
      el = el.parentElement;
    }
    return null; // the window scrolls this page
  }

  scrollPos() {
    const s = this.scroller();
    return s ? s.scrollTop : window.scrollY;
  }

  scrollToPos(y) {
    const s = this.scroller();
    if (s) s.scrollTop = y;
    else window.scrollTo(0, y);
  }

  scrollByPx(delta) {
    if (!delta) return;
    const s = this.scroller();
    if (s) s.scrollTop += delta;
    else window.scrollBy(0, delta);
  }

  /* Where the top of the scrolling viewport is, in client coordinates, so a
     card's position can be expressed the same way whether the container or
     the window is doing the scrolling. */
  viewportTop() {
    const s = this.scroller();
    return s ? s.getBoundingClientRect().top : 0;
  }

  /* The card the visitor is actually looking at — the first one reaching
     into the viewport — and how far down the viewport it sits. This is the
     thing worth preserving across a refresh or an insert; a pixel count is
     not, because the layout it was measured against is still changing. */
  firstVisibleCell() {
    const vTop = this.viewportTop();
    const cells = this.querySelectorAll('[data-af-cell]');
    for (let i = 0; i < cells.length; i++) {
      const rect = cells[i].getBoundingClientRect();
      if (rect.bottom > vTop + 1) return { el: cells[i], index: i, offset: Math.round(rect.top - vTop) };
    }
    return null;
  }

  /* Hold one card at a fixed offset inside that viewport for a beat, while
     lazy images above it change the layout. Released the moment the visitor
     touches the page — never fight them for the scrollbar. */
  holdAnchorAt(anchor, target, frames = 60) {
    if (!anchor) return;
    let n = 0;
    let live = true;
    const release = () => {
      live = false;
      ['wheel', 'touchstart', 'keydown', 'mousedown'].forEach((ev) =>
        window.removeEventListener(ev, release, true)
      );
    };
    ['wheel', 'touchstart', 'keydown', 'mousedown'].forEach((ev) =>
      window.addEventListener(ev, release, { capture: true, passive: true })
    );
    /* Each frame costs a forced layout, so stop as soon as the card has
       held still for a stretch instead of burning the full budget. */
    let settled = 0;
    const tick = () => {
      if (!live || !anchor.isConnected) return release();
      const drift = anchor.getBoundingClientRect().top - this.viewportTop() - target;
      if (Math.abs(drift) > 1) {
        this.scrollByPx(drift);
        settled = 0;
      } else if (++settled >= 15) {
        return release();
      }
      if (++n < frames) requestAnimationFrame(tick);
      else release();
    };
    requestAnimationFrame(tick);
  }

  /* ---------- Restore after a refresh / return (R54) ---------- */

  /* The key ignores af_page so the offset saved at "3 pages in" is the same
     one looked up when those 3 pages come back. */
  static scrollKeyFor(href) {
    const u = new URL(href, window.location.origin);
    u.searchParams.delete(PAGES_PARAM);
    u.searchParams.delete('page');
    return SCROLL_KEY + u.pathname + u.search;
  }

  markPagesLoaded(count) {
    const shown = new URL(window.location.href);
    shown.searchParams.delete('page');
    if (count > 1) shown.searchParams.set(PAGES_PARAM, String(count));
    else shown.searchParams.delete(PAGES_PARAM);
    history.replaceState(history.state, '', shown);
  }

  initRestore() {
    this._scrollKey = AFMainCollection.scrollKeyFor(window.location.href);
    /* pagehide covers reload, in-site navigation and bfcache alike. */
    this._saveScroll = () => {
      try {
        const params = new URL(window.location.href).searchParams;
        if (!params.has(PAGES_PARAM) && !params.has('page')) return;
        /* Save WHICH CARD was at the top of the viewport, not how many
           pixels down the page it was. A pixel offset is only meaningful
           against a finished layout, and on the way back in the cards are
           laid out before their images are — restoring 4200px lands on
           completely different products and then Chrome's scroll anchoring
           carries that mistake down the page as the images arrive. */
        const payload = { y: Math.round(this.scrollPos()) };
        const seen = this.firstVisibleCell();
        if (seen) {
          payload.i = seen.index;
          payload.o = seen.offset;
        }
        sessionStorage.setItem(this._scrollKey, JSON.stringify(payload));
      } catch {}
    };
    window.addEventListener('pagehide', this._saveScroll);

    /* R57: read the saved position UP FRONT, so the restore can happen the
       moment the card it names exists rather than after the last page has
       finished arriving. */
    try {
      const raw = sessionStorage.getItem(this._scrollKey);
      sessionStorage.removeItem(this._scrollKey);
      if (raw) {
        try {
          this._restoreTarget = JSON.parse(raw);
        } catch {
          this._restoreTarget = { y: parseInt(raw, 10) }; // an R54/R55 payload
        }
      }
    } catch {}

    const params = new URLSearchParams(window.location.search);
    const want = parseInt(params.get(PAGES_PARAM), 10);
    if (Number.isFinite(want) && want >= 2) {
      this.backfill(Math.min(want, MAX_BACKFILL));
      return;
    }

    /* R55: a plain '?page=N' link. The server gave us page N and nothing
       above it, so the missing pages go on TOP rather than underneath. */
    const landed = parseInt(params.get('page'), 10);
    if (Number.isFinite(landed) && landed >= 2) {
      this.prefill(Math.min(landed, MAX_BACKFILL));
    }
  }

  /* ---------- Page fetching (R57) ----------
     One page as a parsed document, or null. */
  async fetchPage(p) {
    const url = new URL(window.location.href);
    url.searchParams.delete(PAGES_PARAM);
    url.searchParams.set('page', String(p));
    url.searchParams.set('section_id', this.sectionId);
    try {
      const res = await fetch(url);
      if (!res.ok) return null;
      return new DOMParser().parseFromString(await res.text(), 'text/html');
    } catch {
      return null;
    }
  }

  /* Start every page at once and hand back the promises IN ORDER.
     Restoring three pages was doing two round trips end to end, each of
     them a full ~200KB section render — measured at 1685ms on the live
     store where the same two fetches issued together took 564ms. The
     requests do not depend on each other, so there was never a reason to
     queue them; the browser's own per-host limit is throttle enough. What
     must stay ordered is the INSERTION, and that is what the caller does
     by awaiting these in sequence. */
  fetchPagesInOrder(from, to) {
    const jobs = [];
    for (let p = from; p <= to; p++) jobs.push(this.fetchPage(p));
    return jobs;
  }

  /* Put the visitor back as soon as the card they were on exists, rather
     than after the last page has landed. Later pages append BELOW it, so
     nothing it does can move them again. */
  tryRestore() {
    const target = this._restoreTarget;
    if (!target || !Number.isFinite(target.i)) return false;
    const cells = this.querySelectorAll('[data-af-cell]');
    if (!cells[target.i]) return false;
    this._restoreTarget = null;
    this.holdAnchorAt(cells[target.i], target.o || 0, 90);
    return true;
  }

  /* Fetch pages 1..upTo-1 and put them above what the server rendered,
     holding the visitor's viewport still while it happens. */
  async prefill(upTo) {
    const grid = this.querySelector('[data-af-grid]');
    if (!grid) return;

    this.setAttribute('data-af-restoring', '');

    /* All earlier pages requested together, assembled in order, so the grid
       takes ONE insertion and the scroll is corrected once rather than per
       page. */
    const jobs = this.fetchPagesInOrder(1, upTo - 1);
    const fragment = document.createDocumentFragment();
    let got = 0;

    for (let p = 1; p < upTo; p++) {
      const doc = await jobs[p - 1];
      if (!doc) break;
      const cells = doc.querySelectorAll('[data-af-cell]');
      if (cells.length === 0) break;
      cells.forEach((cell) => fragment.appendChild(cell));
      got = p;
    }

    if (fragment.childNodes.length > 0) {
      /* Measure a card that is already on screen, insert, then shift the
         window by however far that card moved. Anything else — estimating
         row heights, scrolling to an index — drifts as images settle. */
      /* Ninety products arriving above the fold would throw the visitor to
         the top of them. Pin the card they are actually looking at — not
         merely the grid's first child, which may be well above the fold —
         and keep it pinned while those products load their images. */
      const seen = this.firstVisibleCell() || { el: grid.querySelector('[data-af-cell]'), offset: 0 };
      grid.insertBefore(fragment, grid.firstChild);
      this.holdAnchorAt(seen.el, seen.offset, 90);
    }

    /* Only claim the URL once every earlier page is genuinely on screen.
       A partial run leaves 'page=N' alone rather than writing an af_page
       that promises products the grid does not have — a hole in the middle
       of a grid is worse than the dead end we are fixing. */
    if (got === upTo - 1) this.markPagesLoaded(upTo);

    this.removeAttribute('data-af-restoring');

    const live = this.querySelector('[data-af-live]');
    if (live) live.textContent = `${grid.querySelectorAll('[data-af-cell]').length} products shown`;

    document.dispatchEvent(new CustomEvent('af:collection:restored', { detail: { pages: upTo, mode: 'prefill' } }));
  }

  /* Page 1 is already on screen from the server. Append 2..upTo below it.
     Every page is requested at once; only the INSERTION is ordered, since
     that has to match the order the customer saw. */
  async backfill(upTo) {
    const grid = this.querySelector('[data-af-grid]');
    if (!grid) return;

    this.setAttribute('data-af-restoring', '');
    const jobs = this.fetchPagesInOrder(2, upTo);
    let reached = 1;
    let lastWrapHTML = null;

    for (let p = 2; p <= upTo; p++) {
      const doc = await jobs[p - 2];
      if (!doc) break;

      const cells = doc.querySelectorAll('[data-af-cell]');
      if (cells.length === 0) break;
      cells.forEach((cell) => grid.appendChild(cell));
      lastWrapHTML = doc.querySelector('[data-af-loadmore-wrap]')?.innerHTML ?? lastWrapHTML;
      reached = p;

      /* Don't make them wait for pages they are not looking at. */
      this.tryRestore();
    }

    /* The Load More button must now point past the LAST page restored, not
       past page 1 — otherwise the next click re-appends page 2. */
    const wrap = this.querySelector('[data-af-loadmore-wrap]');
    if (wrap && lastWrapHTML !== null) wrap.innerHTML = lastWrapHTML;

    /* A short fetch (products sold out or unpublished since the link was
       made) must not keep advertising pages that no longer exist. */
    if (reached !== upTo) this.markPagesLoaded(reached);

    this.removeAttribute('data-af-restoring');

    const live = this.querySelector('[data-af-live]');
    if (live) live.textContent = `${grid.querySelectorAll('[data-af-cell]').length} products shown`;

    this.restoreScroll();
    document.dispatchEvent(new CustomEvent('af:collection:restored', { detail: { pages: reached } }));
  }

  /* The last word after every page has landed. Usually a no-op, because
     tryRestore() has already pinned the card mid-flight. */
  restoreScroll() {
    if (this.tryRestore()) return;

    const saved = this._restoreTarget;
    if (!saved) return;
    this._restoreTarget = null;

    /* No usable anchor (the result set shrank, or an older payload):
       fall back to the raw offset, re-applied while the page grows into
       it, since one early set just clamps short. */
    const y = saved.y;
    if (!Number.isFinite(y) || y <= 0) return;
    let frames = 0;
    const tick = () => {
      if (Math.abs(this.scrollPos() - y) <= 2) return;
      this.scrollToPos(y);
      if (++frames < 60) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }
}

if (!customElements.get('af-main-collection')) {
  customElements.define('af-main-collection', AFMainCollection);
}